import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useStore } from './store';

import LandingPage    from './pages/LandingPage';
import { LoginPage, SignupPage } from './pages/AuthPages';
import Dashboard      from './pages/Dashboard';
import Settings       from './pages/Settings';
import FoldersPage    from './pages/Folders';
import SearchPage     from './pages/SearchPage';
import Onboarding     from './pages/Onboarding';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword    from './pages/ResetPassword';
import VerifyEmailPage  from './pages/VerifyEmailPage';
import SharePage      from './pages/SharePage';
import NotFound       from './pages/NotFound';
import BlogPost       from './pages/BlogPost';
import PrivacyPage    from './pages/PrivacyPage';
import TermsPage      from './pages/TermsPage';
import CookiesPage    from './pages/CookiesPage';
import AboutPage      from './pages/AboutPage';
import ContactPage    from './pages/ContactPage';
import BlogPage       from './pages/BlogPage';
import ChangelogPage  from './pages/ChangelogPage';
import CareersPage    from './pages/CareersPage';
import TodoPage       from './pages/TodoPage';
import AcademyPage      from './pages/AcademyPage';
import FloatingActions  from './components/ui/FloatingActions';
import InstallBanner  from './components/ui/InstallBanner';
import './index.css';

// ─── Spinner ──────────────────────────────────────────────────────────────────
const Spinner: React.FC = () => (
  <div style={{ display:'flex', alignItems:'center', justifyContent:'center', minHeight:'100vh', background:'#FAFAF8', flexDirection:'column', gap:16 }}>
    <div style={{ width:40, height:40, borderRadius:'50%', border:'3px solid #FAEEDA', borderTopColor:'#F5A623', animation:'spin .7s linear infinite' }}/>
    <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    <p style={{ fontSize:14, color:'#7A7570', fontFamily:"'DM Sans',sans-serif" }}>Loading Jötr…</p>
  </div>
);

// ─── Root redirect — PWA users skip landing page ──────────────────────────────
const RootRedirect: React.FC = () => {
  const token = localStorage.getItem('jötr_token') || localStorage.getItem('jotly_token');
  const isPWA = window.matchMedia('(display-mode: standalone)').matches
    || (window.navigator as any).standalone === true;

  useEffect(() => {
    if (isPWA) {
      window.location.replace(token ? '/dashboard' : '/login');
    }
  }, []); // eslint-disable-line

  if (isPWA) return <Spinner />;
  return <LandingPage />;
};

// ─── Protected route ──────────────────────────────────────────────────────────
const Protected: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isAuthenticated = useStore(s => s.isAuthenticated);
  const authLoading     = useStore(s => s.authLoading);
  const initAuth        = useStore(s => s.initAuth);

  useEffect(() => {
    initAuth();
  }, []); // eslint-disable-line

  if (authLoading) return <Spinner/>;
  if (!isAuthenticated) return <Navigate to="/login" replace/>;
  return <>{children}</>;
};

function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Root — smart redirect for PWA users */}
        <Route path="/"                   element={<RootRedirect />} />

        {/* Auth */}
        <Route path="/login"              element={<LoginPage />} />
        <Route path="/signup"             element={<SignupPage />} />
        <Route path="/forgot-password"    element={<ForgotPassword />} />
        <Route path="/reset-password"     element={<ResetPassword />} />
        <Route path="/verify-email"       element={<VerifyEmailPage />} />
        <Route path="/note/:noteId/share" element={<SharePage />} />

        {/* Protected */}
        <Route path="/welcome"   element={<Protected><Onboarding /></Protected>} />
        <Route path="/dashboard" element={<Protected><Dashboard /><FloatingActions /></Protected>} />
        <Route path="/search"    element={<Protected><SearchPage /></Protected>} />
        <Route path="/folders"   element={<Protected><FoldersPage /></Protected>} />
        <Route path="/settings"  element={<Protected><Settings /></Protected>} />
        <Route path="/tasks"     element={<Protected><TodoPage /></Protected>} />
        <Route path="/academy"   element={<Protected><AcademyPage /></Protected>} />

        {/* Info pages */}
        <Route path="/privacy"    element={<PrivacyPage />} />
        <Route path="/terms"      element={<TermsPage />} />
        <Route path="/cookies"    element={<CookiesPage />} />
        <Route path="/about"      element={<AboutPage />} />
        <Route path="/contact"    element={<ContactPage />} />
        <Route path="/blog"       element={<BlogPage />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/changelog"  element={<ChangelogPage />} />
        <Route path="/careers"    element={<CareersPage />} />

        {/* 404 */}
        <Route path="*" element={<NotFound />} />
      </Routes>

      {/* PWA install banner — shows once for first-time visitors */}
      <InstallBanner />
    </BrowserRouter>
  );
}

export default App;